"use client"

import { useMemo, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import "bootstrap-icons/font/bootstrap-icons.css"
import "./Blog.css"

const categories = ["All", "Food Guides", "Lifestyle", "Katchit"]

export const articles = [
  {
    id: 1,
    slug: "delhi-date-night-dining-guide",
    category: "Food Guides",
    title: "Delhi's perfect date-night dining guide",
    excerpt:
      "From quiet rooftop tables to lively neighbourhood cafés, find the right setting for every kind of evening.",
    image: "/assets/dining new.jpg",
    readTime: "6 min read",
    featured: true,
    points: [
      "Choose the mood first: intimate, lively or relaxed.",
      "Check travel time and table availability before you leave.",
      "Pick a place with a menu both people will genuinely enjoy.",
    ],
    takeaway:
      "The best date-night restaurant is not always the fanciest one. It is the place where the setting, food and pace make it easy to enjoy each other's company.",
  },
  {
    id: 2,
    slug: "one-app-four-ways",
    category: "Katchit",
    title: "One app, four ways to make life easier",
    excerpt:
      "Discover how DineZone, PartyZone, Catering and Courier come together in one simple experience.",
    image: "/assets/all-in-one.png",
    readTime: "4 min read",
    points: [
      "Discover nearby dining options through DineZone.",
      "Plan venues and food for gatherings with PartyZone and Catering.",
      "Move important packages across the city with Courier.",
    ],
    takeaway:
      "Katchit brings everyday plans into one familiar experience, so you spend less time switching between services and more time getting things done.",
  },
  {
    id: 3,
    slug: "stress-free-celebration-plan",
    category: "Lifestyle",
    title: "A stress-free plan for your next celebration",
    excerpt:
      "A practical checklist for choosing a venue, planning food and keeping every guest happy.",
    image: "/assets/home_new.jpeg",
    readTime: "5 min read",
    points: [
      "Set the guest count and budget before choosing a venue.",
      "Build a balanced menu with familiar favourites and a few surprises.",
      "Keep one simple timeline for vendors, food and guest arrivals.",
    ],
    takeaway:
      "A memorable celebration comes from thoughtful choices, not an endless checklist. Get the essentials right and leave room to enjoy your own event.",
  },
  {
    id: 4,
    slug: "smarter-courier-checklist",
    category: "Katchit",
    title: "Send it safely: a smarter courier checklist",
    excerpt:
      "Pack better, share the right details and keep important deliveries moving with confidence.",
    image: "/assets/courier.png",
    readTime: "3 min read",
    points: [
      "Use packaging that matches the size and fragility of the item.",
      "Confirm the receiver's name, number and complete address.",
      "Keep valuable or sensitive items sealed and clearly documented.",
    ],
    takeaway:
      "A few careful checks before pickup can prevent most delivery problems and make the experience smoother for both sender and receiver.",
  },
  {
    id: 5,
    slug: "memorable-local-restaurant",
    category: "Food Guides",
    title: "How to spot a truly memorable local restaurant",
    excerpt:
      "Five simple signs that a neighbourhood favourite deserves a place on your must-visit list.",
    image: "/assets/vendor partner.jpg",
    readTime: "5 min read",
    points: [
      "Look for a focused menu that the kitchen can execute consistently.",
      "Notice how the team handles special requests and busy periods.",
      "Trust places that locals return to, not only one-time hype.",
    ],
    takeaway:
      "Great local restaurants earn loyalty through consistency, warmth and food with a clear point of view. Those qualities matter more than flashy décor.",
  },
  {
    id: 6,
    slug: "make-every-meal-an-occasion",
    category: "Lifestyle",
    title: "Make every meal feel like an occasion",
    excerpt:
      "Easy ideas for turning an everyday lunch or dinner into something worth remembering.",
    image: "/assets/images/offer.jpg",
    readTime: "4 min read",
    points: [
      "Choose one dish or flavour as the centre of the meal.",
      "Put away distractions and give the table a little attention.",
      "Share something new alongside the favourites everyone knows.",
    ],
    takeaway:
      "An occasion does not need a formal invitation. Good food, a little intention and the right company can make an ordinary meal feel special.",
  },
]

const Blog = () => {
  const [activeCategory, setActiveCategory] = useState("All")

  const visibleArticles = useMemo(
    () =>
      activeCategory === "All"
        ? articles
        : articles.filter((article) => article.category === activeCategory),
    [activeCategory],
  )

  return (
    <section className="blog-section" aria-labelledby="blog-heading">
      <div className="blog-orb blog-orb-one" />
      <div className="blog-orb blog-orb-two" />

      <div className="container blog-container">
        <div className="blog-heading-row">
          <div className="blog-heading-copy">
            <span className="blog-eyebrow">
              <i className="bi bi-journal-richtext" aria-hidden="true" />
              The Katchit Edit
            </span>
            <h2 id="blog-heading">Fresh stories for a better day out</h2>
            <p>
              Local food finds, celebration ideas and smarter ways to get things
              done—all curated by Katchit.
            </p>
          </div>

          <a className="blog-app-link" href="#contact">
            Stay in the loop
            <i className="bi bi-arrow-up-right" aria-hidden="true" />
          </a>
        </div>

        <div className="blog-tabs" role="tablist" aria-label="Blog categories">
          {categories.map((category) => (
            <button
              key={category}
              type="button"
              role="tab"
              aria-selected={activeCategory === category}
              className={`blog-tab ${activeCategory === category ? "blog-tab-active" : ""}`}
              onClick={() => setActiveCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>

        <motion.div layout className="blog-grid">
          <AnimatePresence mode="popLayout">
            {visibleArticles.map((article, index) => (
              <motion.article
                layout
                key={article.id}
                className={`blog-card ${article.featured && activeCategory === "All" ? "blog-card-featured" : ""}`}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.97 }}
                transition={{ duration: 0.35, delay: index * 0.04 }}
              >
                <a
                  className="blog-card-overlay"
                  href={`/blog/${article.slug}`}
                  aria-label={`Read ${article.title}`}
                />
                <div className="blog-card-media">
                  <img src={article.image} alt="" />
                  <span className="blog-card-category">{article.category}</span>
                  {article.featured && activeCategory === "All" && (
                    <span className="blog-featured-pill">
                      <i className="bi bi-stars" aria-hidden="true" /> Featured
                    </span>
                  )}
                </div>

                <div className="blog-card-content">
                  <span className="blog-read-time">
                    <i className="bi bi-clock" aria-hidden="true" />
                    {article.readTime}
                  </span>
                  <h3>{article.title}</h3>
                  <p>{article.excerpt}</p>
                  <span className="blog-coming-soon">
                    Read full story
                    <i className="bi bi-arrow-right" aria-hidden="true" />
                  </span>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>

        <div className="blog-note">
          <span className="blog-note-icon">
            <i className="bi bi-lightbulb" aria-hidden="true" />
          </span>
          <div>
            <strong>Have a story worth sharing?</strong>
            <p>Tell us about your favourite local food spot or Katchit experience.</p>
          </div>
          <a href="#contact">Share your story</a>
        </div>
      </div>
    </section>
  )
}

export default Blog
